import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import type { Strategy, StrategyFormData, StrategyType } from '../types';

const J2_EXT = '.j2';

export type StrategyFilter = 'all' | StrategyType;

type ModalMode = 'edit' | 'create' | null;

interface StrategiesData {
  active: Strategy[];
  passive: Strategy[];
}

export const useStrategiesData = () => {
  const [strategies, setStrategies] = useState<StrategiesData>({ active: [], passive: [] });
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [filter, setFilter] = useState<StrategyFilter>('all');
  const [formData, setFormData] = useState<StrategyFormData>({
    name: '',
    type: 'active',
    content: '',
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const loadStrategies = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);

      const strategiesRes = await fetch(`${API_BASE_URL}/api/strategies`, {
        signal: controller.signal,
      });

      if (!strategiesRes.ok) {
        if (strategiesRes.status === 404) {
          throw new Error('Strategies endpoint not found');
        }
        throw new Error(`Failed to load strategies (${strategiesRes.status})`);
      }

      const strategiesData = await strategiesRes.json();

      if (controller.signal.aborted) return;

      if (strategiesData.success) {
        setStrategies(strategiesData.strategies);
      }
    } catch (error) {
      if ((error as any).name === 'AbortError') return;
      console.error('Failed to load strategies:', error);
      toast.error((error as Error).message || 'Failed to load strategies');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadStrategies();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadStrategies]);

  const filteredStrategies = useMemo(() => {
    const active = strategies.active.map((s) => ({ ...s, type: 'active' as const }));
    const passive = strategies.passive.map((s) => ({ ...s, type: 'passive' as const }));

    switch (filter) {
      case 'active':
        return active;
      case 'passive':
        return passive;
      default:
        return [...active, ...passive];
    }
  }, [strategies, filter]);

  const handleEdit = (strategy: Strategy) => {
    setSelectedStrategy(strategy);
    setFormData({
      name: strategy.name,
      type: strategy.type,
      content: strategy.content,
    });
    setModalMode('edit');
  };

  const handleCreate = () => {
    setSelectedStrategy(null);
    setFormData({
      name: '',
      type: 'active',
      content: '# New Strategy\n\nYour strategy description and instructions here...',
    });
    setModalMode('create');
  };

  const handleCloseModal = () => {
    setModalMode(null);
  };

  const strategyKey = (strategy: Strategy) => strategy.fileName.replace(J2_EXT, '');

  const handleSave = async () => {
    try {
      const url = selectedStrategy
        ? `${API_BASE_URL}/api/strategies/${selectedStrategy.type}/${strategyKey(selectedStrategy)}`
        : `${API_BASE_URL}/api/strategies`;

      const method = selectedStrategy ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        toast.success(selectedStrategy ? 'Strategy updated successfully' : 'Strategy created successfully');
        handleCloseModal();
        loadStrategies();
      } else {
        toast.error(data.error || 'Failed to save strategy');
      }
    } catch (error) {
      console.error('Failed to save strategy:', error);
      toast.error((error as Error).message || 'Failed to save strategy');
    }
  };

  const handleDelete = async (strategy: Strategy) => {
    if (!window.confirm(`Are you sure you want to delete "${strategy.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/strategies/${strategy.type}/${strategyKey(strategy)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        toast.success('Strategy deleted successfully');
        loadStrategies();
      } else {
        toast.error(data.error || 'Failed to delete strategy');
      }
    } catch (error) {
      console.error('Failed to delete strategy:', error);
      toast.error((error as Error).message || 'Failed to delete strategy');
    }
  };

  return {
    filteredStrategies,
    loading,
    modalMode,
    selectedStrategy,
    filter,
    setFilter,
    formData,
    setFormData,
    handleEdit,
    handleCreate,
    handleCloseModal,
    handleSave,
    handleDelete,
  };
};
